import { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import * as actions from "../../../Redux/Actions";
import MainCard from "../../../component/MainCard/MainCard";
import SearchBar from "../../../component/SearchBar/SearchBar";
import { showErrorToastMessage } from "../../../component/Toasts/Toasts";
import ScoreTab from "../../Resource Management/Resource Allocation/ResourcePOHistory/ScoreTab";
import "../readable-form.scss";

// Manager "Resource Allocation" view. Same look as the HR Resource Allocation
// list (Region / Client / Project Description / Project Type / Sub Project Code /
// Customer Code / Status) but scoped server-side to the manager's OWN team, with
// the HR-only columns (BU Head, HRBP, Actual/Approved HC) and all billing/PO
// deliberately absent. Expand a project row to its team members and score them.
const ManagerTeamScore = () => {
  const dispatch = useDispatch();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openProjects, setOpenProjects] = useState<Record<string, boolean>>({});

  // Member currently being scored (a single allocation row).
  const [selected, setSelected] = useState<any>(null);

  useEffect(() => {
    setLoading(true);
    dispatch(
      actions.getManagerTeamAllocationsAction((res: any) => {
        if (res?.error) showErrorToastMessage("Couldn't load your team's allocations. Please retry.");
        else setRows(res?.data || []);
        setLoading(false);
      })
    );
  }, [dispatch]);

  // Group the allocation rows into project rows (mirrors the HR list: one row per
  // project, expand to see the people on it).
  const projects = useMemo(() => {
    const q = search.trim().toLowerCase();
    const map: Record<string, { info: any; members: any[] }> = {};
    rows
      .filter((r) =>
        !q ||
        [r.employeeName, r.employeeId, r.designation, r.clientName, r.projectDescription, r.projectType, r.subProjectCode, r.customerCode, r.geography]
          .some((v) => String(v ?? "").toLowerCase().includes(q))
      )
      .forEach((r) => {
        const key = `${r.clientName || ""}||${r.projectDescription || ""}||${r.subProjectCode || ""}`;
        if (!map[key]) map[key] = { info: r, members: [] };
        map[key].members.push(r);
      });
    return Object.keys(map)
      .sort((a, b) => a.localeCompare(b))
      .map((key) => ({ key, ...map[key] }));
  }, [rows, search]);

  const toggle = (key: string) =>
    setOpenProjects((prev) => ({ ...prev, [key]: !prev[key] }));

  const cell = (v: any) => (v === undefined || v === null || v === "" ? "—" : v);

  if (selected) {
    return (
      <MainCard>
        <div className="title-container">
          <h2>Resource Allocation</h2>
        </div>
        <button type="button" className="btn cancel-btn buttonsize mt-2 mb-3" onClick={() => setSelected(null)}>
          ← Back to my team
        </button>
        <h5 className="mb-1">{selected.employeeName}</h5>
        <p className="employeedata mb-3">
          {cell(selected.employeeId)} · {cell(selected.designation)} · {cell(selected.clientName)}
        </p>
        <ScoreTab resourceAllocationRef={selected.resourceAllocationRef} resourceName={selected.employeeName} managerScope />
      </MainCard>
    );
  }

  return (
    <MainCard>
      <div className="title-container">
        <h2>Resource Allocation</h2>
      </div>

      <div className="mt-3 mb-3" style={{ maxWidth: 460 }}>
        <SearchBar
          filter={search}
          setFilter={setSearch}
          onSearchClicked={() => {}}
          placeholderName="Search by client, project, name, ID or designation…"
        />
      </div>

      {loading ? (
        <p>Loading your team…</p>
      ) : projects.length === 0 ? (
        <p className="employeedata">No allocations found for your team.</p>
      ) : (
        <div className="table table-responsive readable-table">
          <table className="table-main table table-sm">
            <thead className="table-head">
              <tr className="table-head-row">
                <th className="table-head-row" scope="col" style={{ width: 40 }}></th>
                <th className="table-head-row" scope="col">Region</th>
                <th className="table-head-row" scope="col">Client</th>
                <th className="table-head-row" scope="col">Project Description</th>
                <th className="table-head-row" scope="col">Project Type</th>
                <th className="table-head-row" scope="col">Sub Project Code</th>
                <th className="table-head-row" scope="col">Customer Code</th>
                <th className="table-head-row" scope="col">Status</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {projects.map(({ key, info, members }) => {
                const isOpen = openProjects[key] ?? false;
                return (
                  <Fragment key={key}>
                    <tr className="table-body-row" style={{ cursor: "pointer" }} onClick={() => toggle(key)}>
                      <td>{isOpen ? "▾" : "▸"}</td>
                      <td>{cell(info.geography)}</td>
                      <td>{cell(info.clientName)}</td>
                      <td>{cell(info.projectDescription)}</td>
                      <td>{cell(info.projectType)}</td>
                      <td>{cell(info.subProjectCode)}</td>
                      <td>{cell(info.customerCode)}</td>
                      <td>{cell(info.status)}</td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={8} style={{ background: "#f7f9fc" }}>
                          <table className="table table-sm mb-0" style={{ background: "transparent" }}>
                            <thead>
                              <tr>
                                <th>Team Member</th>
                                <th>Employee ID</th>
                                <th>Designation</th>
                                <th style={{ width: 120 }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {members.map((m) => (
                                <tr key={m.resourceAllocationRef}>
                                  <td>{cell(m.employeeName)}</td>
                                  <td>{cell(m.employeeId)}</td>
                                  <td>{cell(m.designation)}</td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn add-submit-button buttonsize"
                                      onClick={(e) => { e.stopPropagation(); setSelected(m); }}
                                    >
                                      Score
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </MainCard>
  );
};

export default ManagerTeamScore;
